// ============================================================================
// smartLaunchGate.js — SMART Build 2H (pure; Gate handoff eligibility)
//
// Decides — from the never-PHI, client-readable packet root alone — whether a
// SMART-launched packet can hand off into the EXISTING Gate. The launch session
// is server-only; the packet root is the client-visible source of truth (its
// sourceType + ehrImportStatus reflect the 2F/2G outcome). No second Gate.
//
// Build 2H reuses the existing packet→Gate motion (markPacketDrafting +
// navigate('gate', { packetId })). This helper only gates the action and labels
// the row. It NEVER reads/returns patient values, tokens, or launch context.
//
//   isSmartLaunchPacket(packet)
//   getSmartLaunchHandoffState(packet, { canStartGate, callerTenantId })
//
// Cross-tenant access is enforced by Firestore rules (a client cannot read
// another tenant's packet); the optional callerTenantId check is belt-and-braces.
// ============================================================================

export const SMART_LAUNCH_SOURCE_TYPE = 'ehr_launch';
// Packet-root ehrImportStatus values that mean "EHR candidates are imported and
// ready for Gate review" (set by the Build 2G / 1A import rollup).
export const SMART_LAUNCH_IMPORTED_STATUSES = Object.freeze(['READY', 'PARTIAL']);
// Packet statuses from which the GP may (re)enter the Gate.
const HANDOFF_OPEN_STATUSES = Object.freeze(['INBOX', 'DRAFTING_REFERRAL']);

/** True when the packet originated from a SMART EHR launch (Build 2F). */
export function isSmartLaunchPacket(packet) {
  return !!packet && packet.sourceType === SMART_LAUNCH_SOURCE_TYPE;
}

/**
 * Resolve the Gate-handoff state for a packet row.
 * @param {object} packet  never-PHI packet root projection
 * @param {{ canStartGate?: boolean, callerTenantId?: string|null }} [opts]
 * @returns {{
 *   isSmartLaunch: boolean,
 *   importStatus: string|null,
 *   eligible: boolean,
 *   reason: string|null,         // NOT_SMART_LAUNCH | NOT_GP | TENANT_MISMATCH | PACKET_UNAVAILABLE | NOT_IMPORTED
 *   statusLabel: string|null,    // safe row label (no PHI)
 *   actionLabel: string,         // primary action label
 *   blockMessage: string|null,   // safe alert text when blocked
 * }}
 */
export function getSmartLaunchHandoffState(packet, { canStartGate = false, callerTenantId = null } = {}) {
  const actionLabel = 'Continue to Referral Gate';
  const importStatus = (packet && typeof packet.ehrImportStatus === 'string') ? packet.ehrImportStatus : null;
  const sourceSystem = (packet && typeof packet.ehrSourceSystemLabel === 'string' && packet.ehrSourceSystemLabel.trim())
    ? packet.ehrSourceSystemLabel.trim() : null;
  const sourceTag = sourceSystem ? `EHR launch (${sourceSystem})` : 'EHR launch';

  if (!isSmartLaunchPacket(packet)) {
    return { isSmartLaunch: false, importStatus, eligible: false, reason: 'NOT_SMART_LAUNCH', statusLabel: null, actionLabel, blockMessage: null };
  }

  // Role — referring provider only (mirrors canStartGate = isGpEmployee(role)).
  if (!canStartGate) {
    return { isSmartLaunch: true, importStatus, eligible: false, reason: 'NOT_GP', statusLabel: `${sourceTag} · referring provider only`, actionLabel, blockMessage: 'Only a referring provider can continue this packet to the Gate.' };
  }

  // Tenant — defensive (Firestore rules already deny cross-tenant packet reads).
  if (callerTenantId && packet.tenantId && packet.tenantId !== callerTenantId) {
    return { isSmartLaunch: true, importStatus, eligible: false, reason: 'TENANT_MISMATCH', statusLabel: `${sourceTag} · no access`, actionLabel, blockMessage: 'You do not have access to this launch packet.' };
  }

  // Packet must be open (INBOX or DRAFTING_REFERRAL); never COMPLETED/DELETED/FAILED.
  if (!HANDOFF_OPEN_STATUSES.includes(packet.status)) {
    return { isSmartLaunch: true, importStatus, eligible: false, reason: 'PACKET_UNAVAILABLE', statusLabel: `${sourceTag} · no longer available`, actionLabel, blockMessage: 'This packet is no longer available.' };
  }

  // EHR import must have completed (READY) or partially completed (PARTIAL).
  if (!SMART_LAUNCH_IMPORTED_STATUSES.includes(importStatus)) {
    return { isSmartLaunch: true, importStatus, eligible: false, reason: 'NOT_IMPORTED', statusLabel: `${sourceTag} · import not complete`, actionLabel, blockMessage: 'EHR import has not completed for this packet.' };
  }

  return { isSmartLaunch: true, importStatus, eligible: true, reason: null, statusLabel: `${sourceTag} · imported candidates ready`, actionLabel, blockMessage: null };
}

/**
 * Build 2M — can a SMART-launch packet draft into the Gate WITHOUT READY documents?
 *
 * EHR-launch packets are candidate-driven: they carry ZERO uploaded documents and
 * draft only when imported candidates are ready. This mirrors the server guard
 * functions/packets.draftEligibility.js evaluatePacketDraftEligibility (same rule).
 * Pure — reads only never-PHI packet-root fields; never candidate/patient/PHI values.
 *
 * @param {object} packet  never-PHI packet root projection
 * @returns {{ allowed: boolean, reason: string|null, message: string|null }}
 *   reason: NOT_SMART_LAUNCH | EHR_IMPORT_INCOMPLETE | EHR_NO_CANDIDATES | null
 */
export function canDraftPacketFromEhrLaunch(packet) {
  if (!isSmartLaunchPacket(packet)) {
    return { allowed: false, reason: 'NOT_SMART_LAUNCH', message: null };
  }
  const importStatus = (packet && typeof packet.ehrImportStatus === 'string') ? packet.ehrImportStatus : null;
  if (!SMART_LAUNCH_IMPORTED_STATUSES.includes(importStatus)) {
    return { allowed: false, reason: 'EHR_IMPORT_INCOMPLETE', message: 'EHR import is not complete yet' };
  }
  // ehrImportCandidateCount is the never-PHI rollup from the import service.
  // Require >= 1 only when present (older packets without it still pass).
  if (typeof packet.ehrImportCandidateCount === 'number' && packet.ehrImportCandidateCount < 1) {
    return { allowed: false, reason: 'EHR_NO_CANDIDATES', message: 'No EHR candidates are ready for review' };
  }
  return { allowed: true, reason: null, message: null };
}

/**
 * Build 2O — may the user add OPTIONAL supporting documents to a SMART-launch
 * packet? EHR-launch packets accept supporting documents (labs, referral letter,
 * insurance card) through the EXISTING packet upload + OCR pipeline while the
 * packet is INBOX or DRAFTING_REFERRAL, for users who already have packet-upload
 * permission. Documents stay OPTIONAL — this NEVER affects Gate eligibility
 * (Build 2M): an EHR packet with imported candidates can still enter the Gate
 * with 0 documents. Pure — reads only never-PHI packet-root fields; no PHI.
 *
 * @param {object} packet  never-PHI packet root projection
 * @param {{ canUpload?: boolean }} [opts]  canUpload mirrors the existing packet
 *   upload RBAC (gp_employee / intake_coordinator); the server re-enforces it.
 * @returns {{ allowed: boolean, reason: string|null }}
 *   reason: NOT_SMART_LAUNCH | NO_UPLOAD_PERMISSION | PACKET_UNAVAILABLE | null
 */
export function canAddSupportingDocsToEhrPacket(packet, { canUpload = false } = {}) {
  if (!isSmartLaunchPacket(packet)) return { allowed: false, reason: 'NOT_SMART_LAUNCH' };
  if (!canUpload) return { allowed: false, reason: 'NO_UPLOAD_PERMISSION' };
  if (!HANDOFF_OPEN_STATUSES.includes(packet.status)) return { allowed: false, reason: 'PACKET_UNAVAILABLE' };
  return { allowed: true, reason: null };
}

export default { isSmartLaunchPacket, getSmartLaunchHandoffState, canDraftPacketFromEhrLaunch, canAddSupportingDocsToEhrPacket, SMART_LAUNCH_SOURCE_TYPE, SMART_LAUNCH_IMPORTED_STATUSES };
